import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ReactI18NextChildren } from 'react-i18next'

import { useMutation } from '@tanstack/react-query'
import { Scope } from '@unform/core'
import { Alert } from '@vtex/shoreline'
import moment from 'moment'

import { Button } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import Form from '~/components/unform/Form'
import NameAndTimeInterval from '~/components/unform/FormGroup/NameAndTimeInterval'
import { handleValidationsErrorsAPI, history } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useErrorApiAlert } from '~/hooks/useErrorApiAlert'
import useQueryParams from '~/hooks/useQuery'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import AdvancedSettings from '~/modules/retailMedia/components/CampaignForm/elements/Advanced'
import AudienceTargeting, {
  AudienceTargetingInvalidReasonKey
} from '~/modules/retailMedia/components/CampaignForm/elements/AudienceTargeting'
import { Banner } from '~/modules/retailMedia/components/CampaignForm/elements/Banner'
import CampaignDestinationUrl from '~/modules/retailMedia/components/CampaignForm/elements/CampaignDestinationUrl'
import CampaignTargeting from '~/modules/retailMedia/components/CampaignForm/elements/CampaignTargeting'
import Channel from '~/modules/retailMedia/components/CampaignForm/elements/Channel'
import MetaAccountAccessInfo from '~/modules/retailMedia/components/CampaignForm/elements/MetaAccountAccessInfo'
import { NoLinkedProductsConfirmationModal } from '~/modules/retailMedia/components/CampaignForm/elements/NoLinkedProductsConfirmationModal'
import { OffsiteInvestment } from '~/modules/retailMedia/components/CampaignForm/elements/OffsiteInvestment'
import ProductsSection from '~/modules/retailMedia/components/CampaignForm/elements/ProductsSection'
import ReviewStep from '~/modules/retailMedia/components/CampaignForm/elements/ReviewStep'
import { useCampaignForm } from '~/modules/retailMedia/components/CampaignForm/hooks/useCampaignForm'
import { usePrepareCampaignCreateBody } from '~/modules/retailMedia/components/CampaignForm/hooks/usePrepareCampaignCreateBody'
import { useSubmitErrorMessage } from '~/modules/retailMedia/components/CampaignForm/hooks/useSubmitErrorMessage'
import * as S from '~/modules/retailMedia/components/CampaignForm/styles'
import { CampaignChannel } from '~/modules/retailMedia/constants/campaignChannel'
import {
  createCampaignKey,
  createCampaignService
} from '~/modules/retailMedia/services/campaign'
import { useAppSelector } from '~/store/hooks'

import CampaignType from '../../../../elements/CampaignType'
import DestinationRetailer from '../../../../elements/DestinationRetailer'
import { applyPreparedCampaignBody } from '../../../../helpers/applyPreparedCampaignBody'
import { withDefaultAgeRestrictedSetting } from '../../../helpers'
import { DTOCreateOffsiteBannerCampaign } from './dto'

type CampaignTargetingState =
  | undefined
  | {
      targeting: RetailMediaCampaignSegmentation
      data: { keywords: string[] | null; categories: Tag[] | null }
    }

// Shown both inline (media area still locked) and in the form-level summary
// when AudienceTargeting's required demographics aren't filled yet.
const DEMOGRAPHICS_REQUIRED_KEY =
  'rm:campaign.create.AudienceTargeting.alerts.demographicsRequired'
// Takes {{label}} (the channel-specific gate name -- "Audiência VTEX" or
// "Conversão VTEX") -- the generic wording left the operator guessing which
// of the two gates it meant.
const AUDIENCE_FILE_REQUIRED_KEY =
  'rm:campaign.create.AudienceTargeting.audienceImport.errors.required'

// New flow: each section opens once the ones before it are done. The level
// only ever moves forward, so a section the operator is still editing is never
// collapsed under them because an earlier field went invalid and valid again.
const useSectionSequence = (sectionsDone: boolean[], enabled: boolean) => {
  const [unlockedLevel, setUnlockedLevel] = useState(0)
  const firstPendingSection = sectionsDone.indexOf(false)
  const completedSections =
    firstPendingSection === -1 ? sectionsDone.length : firstPendingSection

  useEffect(() => {
    setUnlockedLevel(level => Math.max(level, completedSections))
  }, [completedSections])

  // `undefined` leaves each panel with its own default (open), which is what
  // the legacy flow and every other caller keep.
  const unlockedFrom = (level: number) =>
    enabled ? unlockedLevel >= level : undefined

  // Single choice sections fold away once picked, and hand over to the next.
  const openUntilDone = (level: number, isDone: boolean) =>
    enabled ? unlockedLevel >= level && !isDone : undefined

  return { unlockedFrom, openUntilDone }
}

// What the panel last reported only counts for the required state it was
// reported under. When media starts (or stops) requiring the URL, the panel
// reports from an effect, one render later; until then the old value is
// ignored, so the section never reads as valid in between. Starts true
// (optional) since there's no media yet to require it for.
const useDestinationUrlValidity = (isRequired: boolean) => {
  const [validity, setValidity] = useState({
    isValid: true,
    forRequired: false
  })

  const reportDestinationUrlValidity = useCallback(
    (isValid: boolean) => setValidity({ isValid, forRequired: isRequired }),
    [isRequired]
  )
  const destinationUrlIsValid =
    validity.forRequired === isRequired ? validity.isValid : !isRequired

  return { destinationUrlIsValid, reportDestinationUrlValidity }
}

type SectionsDoneState = {
  selectedChannel?: string | null
  generalInfoIsValid: boolean
  destinationRetailerIsValid: boolean
  audienceTargetingIsValid: boolean
  mediaBannerIsValid: boolean
  bannersCount: number
  metaCopyIsValid: boolean
  destinationUrlIsValid: boolean
  investmentIsValid: boolean
}

// In the order the sections appear; the sequence stops at the first pending one.
const getSectionsDone = (state: SectionsDoneState) => [
  !!state.selectedChannel && state.generalInfoIsValid,
  state.destinationRetailerIsValid,
  state.audienceTargetingIsValid,
  state.mediaBannerIsValid && state.bannersCount > 0 && state.metaCopyIsValid,
  state.destinationUrlIsValid,
  state.investmentIsValid
]

// New flow: no summary list. Every requirement is already flagged in its own
// section -- the validity badge and required asterisk, the audience and media
// panels' inline alerts, the locked products panel -- so the list only
// repeated them (metaCopyIsValid and the campaign-level destination URL never
// had an entry here either). The legacy flow keeps its list.
const getPendingMessages = (isNewFlow: boolean, messages: StringNull[]) =>
  isNewFlow ? [] : messages.filter(item => !!item)

const BannerFields: React.FC = () => {
  const { toast } = useContext(FeedbackContext)

  const [settingBannerIsValid, setSettingBannerIsValid] = useState(true)
  // Demographics-based validity for the new flow's unified panel (FR-017,
  // FR-017k, FR-017m), replacing CampaignTargeting's keyword/category
  // validity — starts false since region, age range and gender start empty
  // (FR-017h has no analog here: they aren't gated options, they're the
  // panel's hard requirements).
  const [audienceTargetingIsValid, setAudienceTargetingIsValid] =
    useState(false)
  // Every requirement AudienceTargeting is missing, as i18n keys -- starts
  // with demographics since region/age/gender start empty. Kept as a list,
  // not the single highest-priority reason: demographics, a pending
  // audience file and empty interests are independent failures, and
  // showing only one kept blaming demographics even after it was fixed,
  // whenever the real blocker was one of the other two (found in review).
  const [
    audienceTargetingInvalidReasonKeys,
    setAudienceTargetingInvalidReasonKeys
  ] = useState<AudienceTargetingInvalidReasonKey[]>([DEMOGRAPHICS_REQUIRED_KEY])
  const [mediaBannerIsValid, setMediaBannerIsValid] = useState(true)
  const [bannersToTable, setBannersToTable] = useState([])
  // Campaign-level default destination URL only becomes required when some
  // already-accepted media has none of its own to fall back to (FR-022l) —
  // starts true (optional) since there's no media yet to require it for.
  const [investmentIsValid, setInvestmentIsValid] = useState(false)
  const [isNoProductsModalOpen, setIsNoProductsModalOpen] = useState(false)
  // New flow only: sections start collapsed and the next one opens as soon as
  // the previous ones are filled (see `useSectionSequence`).
  const [generalInfoIsValid, setGeneralInfoIsValid] = useState(false)
  const [campaignTargeting, setCampaignTargeting] =
    useState<CampaignTargetingState>()
  // Frozen Publya estimate snapshot (channel, objective, budget informed,
  // numbers returned, capture instant) for settings.projections (FR-038).
  // null on the legacy flow, which never renders OffsiteInvestment's
  // Projections panel.
  const [projections, setProjections] = useState<Record<
    string,
    unknown
  > | null>(null)

  // Bumped so ReviewStep can re-read `formRef.getData()` while already
  // open, instead of only on the next collapse/expand -- unform's fields
  // are ref-based, not reactive, so this is the cheapest way to notice a
  // field changed without lifting every one of them into context.
  //
  // Only gated on allowOffsiteBriefing below (not a no-op otherwise):
  // React's onChange on a native <form> is implemented over the "input"
  // event and bubbles from every text field on every keystroke, not just
  // from checkboxes/radios changing -- ungated, this re-rendered the whole
  // legacy offsite banner flow (product table, banner table, targeting, no
  // memo) on every keystroke too, not just the new flow ReviewStep needs
  // it for (review comment, 2026-09-09, measured: 14 keystrokes -> 14
  // re-renders of this whole tree).
  //
  // Toggle-button fields (region/age/social class/gender, interests)
  // set unform values from `<button onClick>` and never emit a native
  // input, so they never reach this Form onChange. AudienceTargeting
  // calls `onFieldsChange` on those edits so ReviewStep still refreshes
  // while open.
  const [formVersion, setFormVersion] = useState(0)

  const { t } = useAPPTranslation()

  const rmid = useQueryParams().get('rmid')

  const {
    formRef,
    defaultCampaignName,
    costType,
    audienceId,
    setCampaignPeriod,
    handleDebugSubmit,
    toggleDebugMode,
    allowOffsiteBriefing,
    restrictOffsiteFlow,
    selectedChannel,
    selectedSubpublishers = [],
    formIsReady,
    audienceFile,
    audienceImportEnabled
  } = useCampaignForm()

  // Same computation as AudienceTargeting's own audienceImportLabel --
  // needed here too since AUDIENCE_FILE_REQUIRED_KEY's message is shown
  // again in this component's own summary/notReadyMessage.
  const audienceImportLabel =
    selectedChannel === CampaignChannel.META
      ? t('rm:campaign.create.AudienceTargeting.audienceImport.meta.title')
      : t('rm:campaign.create.AudienceTargeting.audienceImport.dv360.title')

  const translateReasonKey = useCallback(
    (key: AudienceTargetingInvalidReasonKey) =>
      key === AUDIENCE_FILE_REQUIRED_KEY
        ? t(key, { label: audienceImportLabel })
        : t(key),
    [t, audienceImportLabel]
  )

  // The campaign-level default destination URL becomes required only in
  // the new flow, and only once some accepted media lacks its own URL to
  // fall back to (FR-022l) — single varejista, single brand only, per the
  // author (no multi-destination fallback to consider).
  const isDestinationUrlRequired =
    allowOffsiteBriefing &&
    bannersToTable.some((item: BannersToTable) => !item.destination_url)

  const { destinationUrlIsValid, reportDestinationUrlValidity } =
    useDestinationUrlValidity(isDestinationUrlRequired)

  // Meta requires headline, description and call to action per creative
  // (OffsiteCreativesList's own note says so) -- without this, nothing
  // blocked a Meta campaign from being submitted, and approved, with none
  // of the three (review comment, PR #753, 2026-09-28).
  const metaCopyIsValid =
    !allowOffsiteBriefing ||
    selectedChannel !== CampaignChannel.META ||
    bannersToTable.every(
      (item: BannersToTable) =>
        !!item.headline?.trim() &&
        !!item.description?.trim() &&
        !!item.call_to_action?.trim()
    )

  const { prepareCampaignCreateBody, isLinkedInsertionOrderReady } =
    usePrepareCampaignCreateBody()

  const destinationRetailerIsValid = selectedSubpublishers.length > 0

  /**
   * Form is ready
   */
  const formIsValid = useMemo<FormIsValidResponse>((): FormIsValidResponse => {
    const bannerMediaIsValid = !!(
      !!mediaBannerIsValid && !!bannersToTable?.length
    )

    // New flow (FR-017k, FR-017m): validity comes from AudienceTargeting's
    // required demographics (region, age range, gender), not from
    // CampaignTargeting's keyword/category radio — that panel isn't
    // rendered at all once the permission is on (allowOffsiteBriefing,
    // same composition as the panel switch below).
    const bannerSettingsIsValid = allowOffsiteBriefing
      ? audienceTargetingIsValid
      : !!settingBannerIsValid

    const legacyBannerSettingMessages: string[] = bannerSettingsIsValid
      ? []
      : [t('rm:campaign.create.alerts.leastOneSegmentationConfiguration')]

    const hasAtLeastOneBannerMessage: StringNull = bannerMediaIsValid
      ? null
      : t('rm:campaign.create.alerts.leastOneBanner')

    const destinationRetailerMessage: StringNull = destinationRetailerIsValid
      ? null
      : t('rm:campaign.create.alerts.destinationRetailerRequired')

    const messages = getPendingMessages(allowOffsiteBriefing, [
      ...legacyBannerSettingMessages,
      hasAtLeastOneBannerMessage,
      destinationRetailerMessage
    ])

    return {
      isValid:
        bannerMediaIsValid &&
        bannerSettingsIsValid &&
        destinationUrlIsValid &&
        destinationRetailerIsValid &&
        metaCopyIsValid,
      messages
    }
  }, [
    bannersToTable?.length,
    mediaBannerIsValid,
    settingBannerIsValid,
    allowOffsiteBriefing,
    audienceTargetingIsValid,
    destinationUrlIsValid,
    destinationRetailerIsValid,
    metaCopyIsValid,
    t
  ])

  const formDataFinal = useMemo(() => {
    const getTargeting = () => {
      if (campaignTargeting?.targeting === 'category') {
        return {
          categories: String(
            campaignTargeting?.data?.categories.map(item => item.id)
          )
        }
      }

      if (campaignTargeting?.targeting === 'keyword') {
        return {
          keywords: campaignTargeting?.data?.keywords
        }
      }

      return {}
    }

    return {
      name: 'name',
      start_date: moment().format('YYYY-MM-DD'),
      type: 'on_site',
      banners: bannersToTable,
      targeting: getTargeting()
    } as Partial<DTOPayloadMinCosts>
  }, [bannersToTable, campaignTargeting])

  /**
   * Form is ready
   * Valor para controlarmos se devemos bloquear o submit
   */
  // `costType` mora em CampaignFormProvider e não reseta ao trocar de ad
  // type/canal -- em publishers com allow_daily_cost_campaign, CostTypeScope
  // (dentro de AdvancedSettings, também renderizado aqui) pode setá-lo para
  // 'daily_cost' mesmo no fluxo novo, o que bypassava investmentIsValid por
  // completo. No fluxo novo (allowOffsiteBriefing) o investimento sempre
  // precisa ser validado -- o atalho de 'daily_cost' só se aplica ao legado
  // (revisão de PR, 2026-09-09).
  //
  // formIsValid.isValid (already includes audienceTargetingIsValid and
  // destinationRetailerIsValid) has to gate submission directly here --
  // OffsiteInvestment only uses it to disable the budget field, and an
  // already-filled budget doesn't revert when that field is disabled --
  // without this, filling investment before finishing segmentation left the
  // button enabled even with the audience gate marked with no file, or
  // demographics incomplete (bug found in manual test, 2026-09-23).
  const formIsReadyToSubmit = useMemo(
    () =>
      ((!allowOffsiteBriefing && costType === 'daily_cost') ||
        investmentIsValid) &&
      isLinkedInsertionOrderReady &&
      formIsValid.isValid,
    [
      investmentIsValid,
      costType,
      isLinkedInsertionOrderReady,
      allowOffsiteBriefing,
      formIsValid.isValid
    ]
  )

  const { unlockedFrom, openUntilDone } = useSectionSequence(
    getSectionsDone({
      selectedChannel,
      generalInfoIsValid,
      destinationRetailerIsValid,
      audienceTargetingIsValid,
      mediaBannerIsValid,
      bannersCount: bannersToTable.length,
      metaCopyIsValid,
      destinationUrlIsValid,
      investmentIsValid
    }),
    allowOffsiteBriefing
  )

  /**
   * Handle products
   */
  const {
    campaign: {
      products: { selectedProducts }
    }
  } = useAppSelector(state => state.retailMedia)

  /**
   * Handle request
   */
  const { submitErrorMessage, resolveSubmitError } = useSubmitErrorMessage()

  const [AlertErrorCampaign, setErrorCampaign] = useErrorApiAlert({
    title: submitErrorMessage
  })

  const { mutate, isPending } = useMutation({
    mutationKey: [createCampaignKey],
    mutationFn: createCampaignService,
    onSuccess: response => {
      history.push(`/retail-media/campaign/view/${response.data.id}`)

      toast.success(t('rm:campaign.create.toast.success.message'))
    },
    onError: error => {
      setErrorCampaign(error)

      handleValidationsErrorsAPI(error, formRef)

      toast.critical(resolveSubmitError(error))
    }
  })

  /**
   * Handle submit
   */
  const { lengthCentsCurrency } = useAppInfo()

  const blockOffsiteSubmit = restrictOffsiteFlow

  const handleSubmit = useCallback(
    async (formData: CreateCampaignBannerFormData) => {
      if (blockOffsiteSubmit) return

      // Sem produtos vinculados não há conversões/NCM: confirmar antes.
      // Reenvio pelo modal (isNoProductsModalOpen já true) segue direto.
      if (!selectedProducts?.length && !isNoProductsModalOpen) {
        setIsNoProductsModalOpen(true)
        return
      }

      const preData = {
        ...formData,
        products: selectedProducts,
        banners: bannersToTable,
        publisher_id: rmid,
        lengthCentsCurrency,
        // Not a registered unform field -- Projections lifts it here
        // directly instead (see setProjections below).
        projections,
        // Same treatment for the audience CSV link (FR-052j): AudienceImport
        // writes it to the shared context after the upload finishes, so it
        // never goes through unform either.
        //
        // Gated on the segmentation option still being marked. Unmarking it keeps
        // the file (FR-017j), so re-marking costs nothing -- but submitting it
        // anyway would hand a personal-data file to the partner for an option the
        // operator turned off.
        audienceFile: audienceImportEnabled ? audienceFile : null,
        // The cost_type Radio is deliberately unmounted in the new flow
        // (FR-027a -- OffsiteInvestment.tsx hides it, the operator never
        // chooses), so Unform never registers it and formData.cost_type
        // is always undefined here -- setFieldValue on an unregistered
        // field is a no-op. Without this, dto.ts's costType default
        // ('standard') would silently omit total_budget from the payload
        // (review comment).
        ...(allowOffsiteBriefing ? { cost_type: 'total_budget' as const } : {})
      }

      const body = DTOCreateOffsiteBannerCampaign(preData)
      const action = handleDebugSubmit || mutate

      applyPreparedCampaignBody(body, prepareCampaignCreateBody, action)
    },
    [
      selectedProducts,
      isNoProductsModalOpen,
      bannersToTable,
      rmid,
      lengthCentsCurrency,
      projections,
      audienceFile,
      audienceImportEnabled,
      allowOffsiteBriefing,
      blockOffsiteSubmit,
      handleDebugSubmit,
      mutate,
      prepareCampaignCreateBody
    ]
  )

  /**
   * Initial data
   */
  const initialData: Partial<CreateCampaignBannerFormData> = {
    type: 'banner_off_site',
    name: defaultCampaignName,
    start_date: moment().format('YYYY-MM-DD'),
    budget: { daily: null, cpc: null, cpm: null },
    placements: { placementsCheck: 'all' },
    audienceToggleWrapper: { audienceToggle: !!audienceId },
    settings: withDefaultAgeRestrictedSetting({
      budget_type: 'fast_as_possible'
    }),
    cost: {
      cost_type: 'cpmcpc'
    },
    subpublishers: {
      subpublishersCheck: 'all'
    },
    cost_type: '',
    // Internal media-cost percentage (super-admin only field); converted to
    // a fraction and sent as `markup_factor` on submit (see dto.ts).
    markup_factor: 60
  }

  const waitForChannel = allowOffsiteBriefing && !selectedChannel
  // Permissions load as `allowOffsiteBriefing === false`. Native autoFocus
  // only runs on mount, so focusing the name during that pending window
  // cannot be undone when the new flow actually applies.
  const skipNameAutofocus = !formIsReady || waitForChannel

  // A single reason renders as plain text; two or more render as a list --
  // one Alert repeating "select region/age/gender" while the audience file
  // was the actual blocker is exactly the confusion this replaces.
  const audienceTargetingSingleReasonMessage: string | undefined =
    audienceTargetingInvalidReasonKeys.length === 1
      ? translateReasonKey(audienceTargetingInvalidReasonKeys[0])
      : undefined

  const audienceTargetingNotReadyMessage =
    audienceTargetingInvalidReasonKeys.length > 1 ? (
      // Alert's content div is a flex row (Shoreline) that sizes a single
      // child to its own content width, not the full row -- without this,
      // the list sat shrink-wrapped to its longest line, leaving unused
      // space to its right instead of spanning the same width as the
      // panel header above it.
      <ul style={{ width: '100%', margin: 0 }}>
        {audienceTargetingInvalidReasonKeys.map(key => (
          <li key={key}>{translateReasonKey(key)}</li>
        ))}
      </ul>
    ) : (
      audienceTargetingSingleReasonMessage
    )

  return (
    <Form
      ref={formRef}
      onSubmit={handleSubmit}
      initialData={initialData}
      onChange={
        allowOffsiteBriefing
          ? () => setFormVersion(version => version + 1)
          : undefined
      }
    >
      <CampaignType />

      {/* Single read point for allowOffsiteBriefing (FR-004e): the new
          flow's entry step exists only behind the permission. */}
      {allowOffsiteBriefing && <Channel />}

      <NameAndTimeInterval
        formRef={formRef}
        labelTitle={t('rm:campaign.create.NameAndTimeInterval.labelTitle')}
        placeholderTitle={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderTitle'
        )}
        hasDescription
        labelDescription={t(
          'rm:campaign.create.NameAndTimeInterval.labelDescription'
        )}
        placeholderDescription={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderDescription'
        )}
        onlyDates
        // End date becomes mandatory as soon as the publisher has the
        // briefing permission — not gated on a channel already being
        // chosen too (FR-012e). Unlike the sections below that need to
        // know DV360 vs Meta specifics, this is a plain on/off rule with
        // nothing channel-specific to flash — waiting for the channel here
        // would only delay a requirement that already applies. Every
        // other caller of this shared component (onsite strategies, and
        // this same one for a publisher without the permission) keeps end
        // date fully optional, unchanged.
        endDateIsRequired={allowOffsiteBriefing}
        showValidBadgeOnSection
        // The alert describes the open-ended campaign that only the legacy
        // flow allows ("without an end date it stays active while there's
        // balance"). On the new flow the end date is mandatory just above,
        // so the alert contradicts the field it sits under — first thing
        // flagged in the 2026-09-15 demo (SPEC 004 item 1.1).
        intervalAlertMessage={
          allowOffsiteBriefing
            ? undefined
            : [t('rm:campaign.create.NameAndTimeInterval.intervalAlertMessage')]
        }
        showEndDateOptional={!allowOffsiteBriefing}
        titlePanel={t('rm:campaign.create.NameAndTimeInterval.titlePanel')}
        onChangeDate={setCampaignPeriod}
        // New flow: after picking offsite, Channel is the next required
        // step. Skip name autofocus while permissions are still loading
        // (`allowOffsiteBriefing` is false until then) and until a
        // channel is chosen — native autoFocus cannot be undone later.
        autoFocus={skipNameAutofocus ? false : 'name'}
        initialCollapsibleState={!skipNameAutofocus}
        onSectionValidityChange={setGeneralInfoIsValid}
      />

      {/* Single choice, so it folds away once picked (same as type and
          channel) and hands over to the next section. */}
      <DestinationRetailer
        initialCollapsibleState={openUntilDone(1, destinationRetailerIsValid)}
      />

      {/* Bifurcation by permission (FR-017k): once allowOffsiteBriefing is
          on, the new flow gets ONE unified "Audience & segmentation" panel
          — demographics, interests and (future) CSV import — replacing
          both the already-created-audience picker (AudienceGroup, nested
          inside CampaignTargeting) and the keyword/category radio. This
          doesn't wait for a channel to be picked too: neither panel has
          anything channel-specific to show meanwhile, so waiting would
          only delay availability that already applies (same composition
          as FR-012e's end date). Neither the prototype
          (apps/offsite/v2.html, block-dv360-audience / block-meta-audience)
          nor Publya's dispatch model has a concept of a reusable, priced,
          pre-built audience asset — that mechanism stays exclusive to a
          publisher without allowOffsiteBriefing, unchanged. */}
      {!allowOffsiteBriefing && (
        <CampaignTargeting
          handleSectionValidity={setSettingBannerIsValid}
          onChange={setCampaignTargeting}
          formRef={formRef}
        />
      )}

      {allowOffsiteBriefing && (
        <AudienceTargeting
          handleSectionValidity={setAudienceTargetingIsValid}
          onInvalidReasonChange={setAudienceTargetingInvalidReasonKeys}
          onFieldsChange={() => setFormVersion(version => version + 1)}
          initialCollapsibleState={unlockedFrom(2)}
        />
      )}

      <Scope path="banner">
        <Banner
          formRef={formRef}
          bannersToTable={bannersToTable}
          setBannersToTable={setBannersToTable}
          handleSectionValidity={setMediaBannerIsValid}
          selectedSegmentation={campaignTargeting?.targeting}
          initialData={{ publisher_id: rmid }}
          type={'banner_off_site'}
          // Media upload only unlocks once segmentation is concluded, on
          // either flow (FR-017l): the new flow has no "type chosen" step
          // to mirror `selectedSegmentation` — the demographics being
          // filled is the readiness signal there. The legacy flow keeps
          // Banner's own default rule (a targeting type picked in
          // CampaignTargeting).
          isSegmentationReady={
            allowOffsiteBriefing
              ? audienceTargetingIsValid
              : !!campaignTargeting?.targeting
          }
          notReadyMessage={
            allowOffsiteBriefing ? audienceTargetingNotReadyMessage : undefined
          }
          initialCollapsibleState={unlockedFrom(3)}
        />
      </Scope>

      <CampaignDestinationUrl
        isRequired={isDestinationUrlRequired}
        handleSectionValidity={reportDestinationUrlValidity}
        unlocked={unlockedFrom(4)}
      />

      <ProductsSection
        hasRetailer={destinationRetailerIsValid}
        initialCollapsibleState={unlockedFrom(5)}
        formRef={formRef}
        publisherId={rmid}
      />

      {!!formIsValid?.messages?.length && (
        <S.WrapperAlerts>
          {formIsValid?.messages?.map(
            (message: { text: string; action?: () => void }, index) => (
              <Alert
                variant="warning"
                key={index}
                onClick={
                  (message as unknown as { action?: () => void })?.action
                }
                role={
                  (message as unknown as { action?: () => void })?.action
                    ? 'button'
                    : 'text'
                }
              >
                {message?.text || (message as unknown as ReactI18NextChildren)}
              </Alert>
            )
          )}
        </S.WrapperAlerts>
      )}

      <hr />

      <OffsiteInvestment
        publisherId={rmid}
        formRef={formRef}
        formDataFinal={formDataFinal}
        formIsValid={formIsValid}
        setSectionIsValid={setInvestmentIsValid}
        sectionIsValid={investmentIsValid}
        onProjectionsChange={setProjections}
        initialCollapsibleState={unlockedFrom(5)}
      />

      {/* Advanced settings (cost type, placements, budget pace) only apply
          to the legacy keyword/category flow's own fields — the new offsite
          briefing flow has no analog for them, same composition as
          CampaignTargeting above. */}
      {!allowOffsiteBriefing && (
        <AdvancedSettings targeting={campaignTargeting?.targeting} />
      )}

      {/* Last section before submitting (FR-023 to FR-023c) — read-only,
          not the approver-facing review from 005-offsite-campaign-approval.
          Same composition as the rest of the flow (FR-017k) — permission
          alone, not waiting for a channel too: ReviewStep's channel row
          already renders "—" gracefully when there's none yet, so there's
          nothing to gain from waiting, and waiting depended on
          `selectedChannel`, which doesn't reset when the operator leaves
          Offsite Banner and comes back within the same session (it lives in
          CampaignFormProvider, above the ad-type dispatch) — a stale value
          from an earlier visit could show this row before the Channel
          radio was picked again this time. */}
      {allowOffsiteBriefing && (
        <ReviewStep
          formRef={formRef}
          bannersToTable={bannersToTable}
          projections={projections}
          formVersion={formVersion}
          initialCollapsibleState={unlockedFrom(6)}
        />
      )}

      {/* Meta-exclusive info panel (FR-024a to FR-024c) — purely
          informational, no field, no persistence. Last block on the page
          (after the review), matching the prototype's block order
          (ads-design-prototypes, apps/offsite/v2.html: block-meta-account
          comes after block-meta-review). Explicit permission check kept
          alongside the channel check (rather than relying only on Channel
          never rendering without it) so this never shows if
          `allowOffsiteBriefing` flips after a channel was already picked. */}
      {allowOffsiteBriefing && selectedChannel === CampaignChannel.META && (
        <MetaAccountAccessInfo />
      )}

      <AlertErrorCampaign />

      {!!handleDebugSubmit && (
        <Alert variant="critical">
          🚀 ~ DEBUG MODE ATIVADO: este modo imprime o payload da requisição no
          console e desabilita o envio do formulário.
        </Alert>
      )}

      <div className="form-default__buttons">
        {!!handleDebugSubmit && (
          <Button
            type="button"
            text={'Desativar modo debug'}
            onClick={toggleDebugMode}
            size="large"
            template="critical"
          />
        )}

        <Button
          type="submit"
          text={t('rm:sendForApproval')}
          loading={isPending}
          size="large"
          template="primary"
          disabled={!formIsReadyToSubmit || blockOffsiteSubmit}
        />
      </div>

      <NoLinkedProductsConfirmationModal
        isModalOpen={isNoProductsModalOpen}
        setIsModalOpen={setIsNoProductsModalOpen}
        formRef={formRef}
        isPending={isPending}
      />
    </Form>
  )
}

export default BannerFields
